import React, { useState } from 'react';
import { MissionIcon, VisionIcon, ValuesIcon, EmailIcon, LocationIcon, CheckIcon } from '../components/Icons';
import { submitContactMessage } from '../services/orderPortal';
import { CONTACT_EMAIL } from '../config/site';
import { BtnContent } from '../components/BtnIcon';
import { IoSendOutline } from 'react-icons/io5';

function About() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validateForm()) return;

    setSubmitting(true);
    const res = await submitContactMessage({
      name: formData.name,
      email: formData.email,
      message: formData.message,
      source: 'about',
    });
    setSubmitting(false);
    if (!res.ok) {
      setSubmitError(res.message || 'Could not send your message. Try again or email us directly.');
      return;
    }
    setSubmitted(true);
    setFormData({ name: '', email: '', message: '' });
    setTimeout(() => setSubmitted(false), 5000);
  };
  return (
    <div className="container">
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <h1 className="section-title">About Rencin Gifting</h1>
        
        <div style={{
          background: 'var(--ivory-warm)',
          padding: '3rem',
          borderRadius: '20px',
          boxShadow: '0 8px 30px rgba(58, 58, 56, 0.08)',
          marginBottom: '3rem'
        }}>
          <h2 style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '2rem',
            marginBottom: '1.5rem',
            color: 'var(--accent-primary)',
            fontWeight: 600
          }}>
            Our Story
          </h2>
          <p style={{
            fontSize: '1.1rem',
            lineHeight: '1.8',
            color: 'var(--text-primary)',
            marginBottom: '1.5rem'
          }}>
            Rencin Gifting was born from a love for thoughtful surprises and beautifully wrapped moments. We believe every celebration deserves a gift that feels personal, polished, and memorable before it is even opened.
          </p>
          <p style={{
            fontSize: '1.1rem',
            lineHeight: '1.8',
            color: 'var(--text-primary)',
            marginBottom: '1.5rem'
          }}>
            Our journey began with a simple vision: to make gifting easier without losing warmth. From festive hampers to corporate boxes, our collections are designed to help you send joy with confidence.
          </p>
        </div>

        <div className="features" style={{ marginTop: '3rem' }}>
          <div className="feature">
            <div className="feature-icon">
              <MissionIcon size={48} color="var(--accent-primary)" />
            </div>
            <h3>Our Mission</h3>
            <p>To make premium gifting simple, personal, and celebration-ready for every occasion.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">
              <VisionIcon size={48} color="var(--accent-primary)" />
            </div>
            <h3>Our Vision</h3>
            <p>To become the trusted destination for curated gift boxes, festive hampers, and modern gifting experiences.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">
              <ValuesIcon size={48} color="var(--accent-primary)" />
            </div>
            <h3>Our Values</h3>
            <p>Thoughtful curation, reliable service, beautiful presentation, and a commitment to making every recipient feel seen.</p>
          </div>
        </div>

        <div style={{
          background: 'var(--ivory-warm)',
          padding: '3rem',
          borderRadius: '20px',
          boxShadow: '0 8px 30px rgba(58, 58, 56, 0.08)',
          marginTop: '3rem'
        }}>
          <h2 style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '2rem',
            marginBottom: '1.5rem',
            color: 'var(--accent-primary)',
            fontWeight: 600
          }}>
            Why Choose Us
          </h2>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            fontSize: '1.1rem',
            lineHeight: '2'
          }}>
            <li style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <span style={{ display: 'flex', alignItems: 'center', color: 'var(--accent-primary)' }}>
                <CheckIcon size={20} color="var(--accent-primary)" />
              </span>
              <span>Handpicked products curated around real gifting moments</span>
            </li>
            <li style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <span style={{ display: 'flex', alignItems: 'center', color: 'var(--accent-primary)' }}>
                <CheckIcon size={20} color="var(--accent-primary)" />
              </span>
              <span>Detail-led packing with ribbons, inserts, and premium finishes</span>
            </li>
            <li style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <span style={{ display: 'flex', alignItems: 'center', color: 'var(--accent-primary)' }}>
                <CheckIcon size={20} color="var(--accent-primary)" />
              </span>
              <span>Festive, corporate, and personal boxes for every budget</span>
            </li>
            <li style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <span style={{ display: 'flex', alignItems: 'center', color: 'var(--accent-primary)' }}>
                <CheckIcon size={20} color="var(--accent-primary)" />
              </span>
              <span>Customization services for names, notes, themes, and branding</span>
            </li>
            <li style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <span style={{ display: 'flex', alignItems: 'center', color: 'var(--accent-primary)' }}>
                <CheckIcon size={20} color="var(--accent-primary)" />
              </span>
              <span>Secure packaging and dependable delivery support</span>
            </li>
          </ul>
        </div>

        <div style={{
          textAlign: 'center',
          padding: '3rem 2rem',
          marginTop: '3rem'
        }}>
          <h2 style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '2rem',
            marginBottom: '1rem',
            color: 'var(--accent-primary)',
            fontWeight: 600
          }}>
            Get in Touch
          </h2>
          <p style={{
            fontSize: '1.1rem',
            color: 'var(--text-secondary)',
            marginBottom: '2rem'
          }}>
            We'd love to hear from you! Whether you have questions about our collections or need assistance with a custom order, we're here to help.
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '3rem',
            maxWidth: '1000px',
            margin: '0 auto',
            alignItems: 'start'
          }}>
            {/* Contact Information */}
            <div style={{
              textAlign: 'left',
              background: 'var(--ivory-warm)',
              padding: '2.5rem',
              borderRadius: '15px',
              boxShadow: '0 8px 30px rgba(58, 58, 56, 0.08)'
            }}>
              <h3 style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '1.5rem',
                marginBottom: '1.5rem',
                color: 'var(--accent-primary)',
                fontWeight: 600
              }}>
                Contact Information
              </h3>
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{
                  fontSize: '0.9rem',
                  color: 'var(--text-secondary)',
                  marginBottom: '0.5rem',
                  fontWeight: 600
                }}>
                  Email
                </p>
                <a href={`mailto:${CONTACT_EMAIL}`} style={{
                  color: 'var(--accent-primary)',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <EmailIcon size={18} color="var(--accent-primary)" />
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div>
                <p style={{
                  fontSize: '0.9rem',
                  color: 'var(--text-secondary)',
                  marginBottom: '0.5rem',
                  fontWeight: 600
                }}>
                  Address
                </p>
                <p style={{
                  color: 'var(--text-primary)',
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <LocationIcon size={18} color="var(--text-primary)" />
                  Mumbai, India
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div style={{
              background: 'var(--ivory-warm)',
              padding: '2.5rem',
              borderRadius: '15px',
              boxShadow: '0 8px 30px rgba(58, 58, 56, 0.08)'
            }}>
              <h3 style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '1.5rem',
                marginBottom: '1.5rem',
                color: 'var(--accent-primary)',
                fontWeight: 600,
                textAlign: 'left'
              }}>
                Send us a Message
              </h3>
              {submitted ? (
                <div style={{
                  padding: '2rem',
                  background: 'rgba(6, 29, 58, 0.1)',
                  borderRadius: '10px',
                  color: 'var(--accent-primary)',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <CheckIcon size={24} color="var(--accent-primary)" />
                  <span>Thank you! Your message has been sent. We'll get back to you soon.</span>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: '1.5rem', textAlign: 'left' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      color: 'var(--text-primary)'
                    }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '0.9rem',
                        border: `2px solid ${errors.name ? '#d32f2f' : 'var(--border-color)'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)'
                      }}
                    />
                    {errors.name && (
                      <span style={{ color: '#d32f2f', fontSize: '0.85rem', display: 'block', marginTop: '0.3rem' }}>
                        {errors.name}
                      </span>
                    )}
                  </div>
                  
                  <div style={{ marginBottom: '1.5rem', textAlign: 'left' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      color: 'var(--text-primary)'
                    }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '0.9rem',
                        border: `2px solid ${errors.email ? '#d32f2f' : 'var(--border-color)'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)'
                      }}
                    />
                    {errors.email && (
                      <span style={{ color: '#d32f2f', fontSize: '0.85rem', display: 'block', marginTop: '0.3rem' }}>
                        {errors.email}
                      </span>
                    )}
                  </div>
                  
                  <div style={{ marginBottom: '1.5rem', textAlign: 'left' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      color: 'var(--text-primary)'
                    }}>
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      style={{
                        width: '100%',
                        padding: '0.9rem',
                        border: `2px solid ${errors.message ? '#d32f2f' : 'var(--border-color)'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        resize: 'vertical'
                      }}
                    />
                    {errors.message && (
                      <span style={{ color: '#d32f2f', fontSize: '0.85rem', display: 'block', marginTop: '0.3rem' }}>
                        {errors.message}
                      </span>
                    )}
                  </div>
                  
                  {submitError && (
                    <p style={{ color: '#d32f2f', fontSize: '0.9rem', marginBottom: '1rem', textAlign: 'left' }}>
                      {submitError}
                    </p>
                  )}
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '1rem'
                    }}
                  >
                    <BtnContent icon={IoSendOutline}>
                      {submitting ? 'Sending…' : 'Send Message'}
                    </BtnContent>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default About;

